import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";

export const CartContext = createContext(null);

const readStorage = (key, fallback) => {
  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStorage(siteConfig.storageKeys.cart, [])
  );
  const [wishlist, setWishlist] = useState(() =>
    readStorage(siteConfig.storageKeys.wishlist, [])
  );
  const [promoCode, setPromoCode] = useState(() =>
    readStorage(siteConfig.storageKeys.promo, "")
  );
  const [notice, setNotice] = useState("");

  useEffect(() => {
    window.localStorage.setItem(siteConfig.storageKeys.cart, JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    window.localStorage.setItem(
      siteConfig.storageKeys.wishlist,
      JSON.stringify(wishlist)
    );
  }, [wishlist]);

  useEffect(() => {
    window.localStorage.setItem(
      siteConfig.storageKeys.promo,
      JSON.stringify(promoCode)
    );
  }, [promoCode]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(""), 2600);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const addToCart = useCallback((productId, quantity = 1, variant = "") => {
    const safeQuantity = Math.max(1, Number(quantity) || 1);
    setCartItems((items) => {
      const existing = items.find(
        (item) => item.id === productId && item.variant === variant
      );
      if (existing) {
        return items.map((item) =>
          item.id === productId && item.variant === variant
            ? { ...item, quantity: item.quantity + safeQuantity }
            : item
        );
      }
      return [...items, { id: productId, quantity: safeQuantity, variant }];
    });
    const product = productData.find((item) => item.id === productId);
    setNotice(`${product?.name || "Item"} added to your cart.`);
  }, []);

  const removeFromCart = useCallback((productId, variant = "") => {
    setCartItems((items) =>
      items.filter(
        (item) => !(item.id === productId && item.variant === variant)
      )
    );
  }, []);

  const updateQuantity = useCallback((productId, variant = "", quantity) => {
    const safeQuantity = Math.max(1, Number(quantity) || 1);
    setCartItems((items) =>
      items.map((item) =>
        item.id === productId && item.variant === variant
          ? { ...item, quantity: safeQuantity }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
    setPromoCode("");
  }, []);

  const addWishlist = useCallback((productId) => {
    setWishlist((items) =>
      items.includes(productId) ? items : [...items, productId]
    );
    setNotice("Saved to your favorites.");
  }, []);

  const removeWishlist = useCallback((productId) => {
    setWishlist((items) => items.filter((id) => id !== productId));
  }, []);

  const toggleWishlist = useCallback((productId) => {
    setWishlist((items) => {
      const saved = items.includes(productId);
      setNotice(saved ? "Removed from favorites." : "Saved to your favorites.");
      return saved
        ? items.filter((id) => id !== productId)
        : [...items, productId];
    });
  }, []);

  const detailedItems = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const cartCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );
  const subtotal = useMemo(
    () =>
      detailedItems.reduce(
        (total, item) => total + item.product.price * item.quantity,
        0
      ),
    [detailedItems]
  );
  const discount = promoCode === "WELCOME10" ? subtotal * 0.1 : 0;

  const value = useMemo(
    () => ({
      cartItems,
      detailedItems,
      cartCount,
      subtotal,
      promoCode,
      discount,
      wishlist,
      notice,
      addToCart,
      addCart: addToCart,
      removeFromCart,
      removeCart: removeFromCart,
      updateQuantity,
      clearCart,
      setPromoCode,
      addWishlist,
      removeWishlist,
      toggleWishlist,
    }),
    [
      cartItems,
      detailedItems,
      cartCount,
      subtotal,
      promoCode,
      discount,
      wishlist,
      notice,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      setPromoCode,
      addWishlist,
      removeWishlist,
      toggleWishlist,
    ]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
      <div
        className={`shop-notice ${notice ? "shop-notice--visible" : ""}`}
        role="status"
        aria-live="polite"
      >
        {notice}
      </div>
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within CartProvider");
  return context;
};
